/* eslint-disable jira/naming/disallow-filenames */
// eslint-disable-next-line jira/filename-case
import React, { type KeyboardEvent, useState, useEffect, useCallback } from 'react';
import debounce from 'lodash/debounce';
import noop from 'lodash/noop';
import EnterEscapeHandler from '@jira/platform__enter-escape-handler/src/index.tsx';
import Avatar from '@atlaskit/avatar/avatar';
import Flex from '@atlaskit/primitives/components/flex';
import { xcss } from '@atlaskit/primitives/xcss';
import Box from '@atlaskit/primitives/box';
import type { MultiValue } from '@atlaskit/react-select/types';
import AsyncSelect from '@atlaskit/select/async-select';
import { useIntl, defineMessage } from '@atlassian/jira-intl';
import { PICKER_SEARCH_DEBOUNCE_TIME } from './boardTypes.constants.tsx';
import type {
	FieldValueMultiPickerProps,
	JQLTermType,
	GenericPickerOption,
} from './BoardCreateFormValues.types.tsx';
import { useLoadOptionsController } from './useLoadOptionsController.tsx';

const genericPickerPlaceholder = defineMessage({
	id: 'software-board-create.modal.form.source-value-picker.generic-picker.generic-picker-placeholder',
	defaultMessage: 'Select',
	description:
		'Placeholder text shown in the generic multi-value picker dropdown before a selection is made',
});

export type GenericPickerProps = {
	jqlTerm: JQLTermType;
	label: string;
} & FieldValueMultiPickerProps<MultiValue<GenericPickerOption>>;

const formatOptionLabel = (option: GenericPickerOption) => (
	<Flex alignItems="center">
		{option.avatar && (
			<Flex xcss={imgStyles}>
				<Avatar appearance="square" size="xsmall" src={option.avatar} borderColor="transparent" />
			</Flex>
		)}
		<Box xcss={optionStyles}>{option.label}</Box>
	</Flex>
);

export const GenericPicker = ({
	onChange,
	value,
	jqlTerm,
	isInvalid,
	label,
}: GenericPickerProps): React.JSX.Element => {
	const { loadOptions, isLoading } = useLoadOptionsController();
	const [defaultOptions, setDefaultOptions] = useState<GenericPickerOption[] | undefined>();
	const [menuIsOpen, setMenuIsOpen] = useState<boolean>(false);
	const { formatMessage } = useIntl();

	useEffect(() => {
		loadOptions(jqlTerm)('').then((options) => setDefaultOptions(options));
	}, [jqlTerm, loadOptions]);

	const debouncedLoadOptions = debounce(
		(searchString, callback) =>
			loadOptions(jqlTerm)(searchString).then((options) => callback(options)),
		PICKER_SEARCH_DEBOUNCE_TIME,
	);

	const handleOnKeyDown = useCallback(
		(event: KeyboardEvent<EventTarget>) => {
			if (!menuIsOpen && (event.key === 'ArrowUp' || event.key === 'ArrowDown')) {
				setMenuIsOpen(true);
			}
			// close the menu when focusing on multi value to prevent enter key from propagating
			if (event.key === 'ArrowLeft' || event.key === 'ArrowRight') {
				setMenuIsOpen(false);
			}
			// prevent form submission error
			if (event.key === 'Enter' && !menuIsOpen) {
				event.preventDefault();
			}
		},
		[menuIsOpen],
	);

	return (
		/* Prevent ESC press on Select bubbling up to the modal */
		<EnterEscapeHandler onEscape={noop}>
			<AsyncSelect
				aria-label={label}
				isMulti
				isLoading={isLoading}
				formatOptionLabel={formatOptionLabel}
				defaultOptions={defaultOptions}
				loadOptions={debouncedLoadOptions}
				onChange={onChange}
				value={value}
				placeholder={formatMessage(genericPickerPlaceholder)}
				menuPosition="fixed"
				classNamePrefix="source-value-picker"
				isInvalid={isInvalid}
				onFocus={() => setMenuIsOpen(true)}
				onBlur={() => setMenuIsOpen(false)}
				menuIsOpen={menuIsOpen}
				blurInputOnSelect={false}
				onKeyDown={handleOnKeyDown}
			/>
		</EnterEscapeHandler>
	);
};

const imgStyles = xcss({
	paddingRight: 'space.100',
});

const optionStyles = xcss({
	textOverflow: 'ellipsis',
	overflow: 'hidden',
	whiteSpace: 'nowrap',
});
